// File: practicesForPort.js
import React, { useState, useEffect } from 'react';

/**
 * Dashboard view that lists practices missing a retell number.
 * • Three filter-tabs (Not Submitted / Submitted / Finished) at the top.
 * • A “Stage ➜” button in each row cycles the practice to the next stage
 *   without opening the modal.
 * • Clicking anywhere else in a row still opens the detail modal.
 */
export default function PracticesForPort() {
//const API_ROOT = 'https://dentalexpressserver.azurewebsites.net/api';
const API_ROOT = 'http://localhost:3001/api'; // ← local dev

  /* ---------- list + stage filter ---------- */
  const [stageTab,   setStageTab]   = useState('notSubmitted');
  const [practices,  setPractices]  = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  /* ---------- details modal ---------- */
  const [detail,        setDetail]        = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState('');

  /* ---------- inline stage update ---------- */
  const [stageBusyId, setStageBusyId] = useState(null);

  const nextStage = (s) =>
    s === 'notSubmitted' ? 'submitted' : s === 'submitted' ? 'finished' : 'finished';

  const updateStage = async (practiceId, currentStage) => {
    const newStage = nextStage(currentStage);
    setStageBusyId(practiceId);
    try {
      await fetch(`${API_ROOT}/practice-port-stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ practiceId, stage: newStage }),
      });
      // update list + modal immediately
      setPractices((prev) =>
        prev.map((p) =>
          p.practiceId === practiceId ? { ...p, portStage: newStage } : p
        )
      );
      if (detail?.practiceId === practiceId) {
        setDetail({ ...detail, portStage: newStage });
      }
    } catch (e) {
      alert('Stage update failed: ' + e.message);
    } finally {
      setStageBusyId(null);
    }
  };

  /* ---------- fetch list ---------- */
  const fetchPractices = async () => {
    setLoading(true);
    setError('');
    const options = {
      headers: {
        "Authorization": `Bearer ${localStorage.getItem('jwtToken')}`,
      },
    };
    try {
      const res = await fetch(
        `${API_ROOT}/practices-missing-retell?stage=${encodeURIComponent(stageTab)}`, options
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) setPractices(data.practices);
      else throw new Error(data.error || 'Unknown error');
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- fetch details ---------- */
  const fetchDetails = async (practiceId) => {
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const res = await fetch(
        `${API_ROOT}/practice-port-details?practiceId=${encodeURIComponent(practiceId)}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) setDetail(data.data);
      else throw new Error(data.error || 'Unknown error');
    } catch (e) {
      console.error(e);
      setDetailError(e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  /* ---------- fetch whenever tab changes ---------- */
  // eslint-disable-next-line
  useEffect(() => { fetchPractices(); /* eslint-disable-next-line */ }, [stageTab]);

  const closeModal = () => setDetail(null);

  /* ---------- render ---------- */
  return (
    <div className="tab-content">
      <h1>Practices Missing Retell #</h1>

      {/* stage filter tabs */}
      <div style={{ marginBottom: '1rem' }}>
        {['notSubmitted', 'submitted', 'finished'].map((s) => (
          <button
            key={s}
            onClick={() => setStageTab(s)}
            className={stageTab === s ? 'active' : ''}
            style={{ marginRight: 8 }}
          >
            {s === 'notSubmitted' ? 'Not Submitted' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      <button onClick={fetchPractices} disabled={loading}>
        {loading ? 'Loading…' : 'Refresh'}
      </button>

      {error && <div className="error-message">{error}</div>}

      {!loading && !error && (
        <>
          <p><strong>Total:</strong> {practices.length}</p>
          <table>
            <thead>
              <tr>
                <th>Practice&nbsp;ID</th>
                <th>Name</th>
                <th>Stage</th>
              </tr>
            </thead>
            <tbody>
              {practices.map((p) => {
                const stage = p.portStage || 'notSubmitted';
                return (
                  <tr key={p.practiceId} onClick={() => fetchDetails(p.practiceId)}>
                    <td>{p.practiceId}</td>
                    <td>{p.name}</td>
                    <td>
                      {stage}{' '}
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // keep row click from firing
                          updateStage(p.practiceId, stage);
                        }}
                        disabled={stageBusyId === p.practiceId}
                        title="Advance stage"
                      >
                        ➜
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* modal */}
      {detail && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{detail.practiceName}</h2>
            <p style={{ marginTop: -10, marginBottom: 10 }}>
              <strong>Stage:</strong> {detail.portStage}
            </p>

            <ul className="detail-list">
              <li><strong>Billing phone:</strong> {detail.billingPhone}</li>
              <li><strong>Numbers to port:</strong> {detail.numbersToPort.length ? detail.numbersToPort.join(', ') : '—'}</li>
              <li><strong>Address:</strong> {detail.address}</li>
              <li><strong>PIN:</strong> {detail.pin}</li>
              <li><strong>Account #:</strong> {detail.accountNumber}</li>
              <li><strong>Admin user:</strong> {detail.adminUser}</li>

              {detail.invoiceUrl && (
                <li>
                  <a href={detail.invoiceUrl} target="_blank" rel="noopener noreferrer">
                    📄 Download Invoice PDF
                  </a>
                </li>
              )}
              {detail.loaUrl && (
                <li>
                  <a href={detail.loaUrl} target="_blank" rel="noopener noreferrer">
                    📄 Download LOA PDF
                  </a>
                </li>
              )}
            </ul>
            <button onClick={closeModal}>Close</button>
          </div>
        </div>
      )}

      {detailLoading && <div className="loading-overlay">Loading details…</div>}
      {detailError && <div className="error-message">{detailError}</div>}
    </div>
  );
}
